import { computed, onUnmounted, ref } from "@vue/composition-api";
import { isEqual } from "lodash-es";
import { WebRTCConnection } from "@scrile/api-provider/dist/api/WebRTCProvider";
import { StreamingHelper } from "@scrile/streaming-client";
import providers from "@/lib/api";
import { LoggerContextWithMessage } from "@/modules/callsLogger";
import { MediaConstraints } from "@/types";
import useStreamLogger from "./useStreamLogger";

export function useProduce(getAdditionalLoggerContext: () => Record<string, any>) {
  const stream = ref<MediaStream>(new MediaStream([]));
  const onCreatedPromise = ref<Promise<void>>();
  const connecting = ref(true);
  const connectingState = ref("");
  const connectionData = ref<WebRTCConnection | null>(null);
  const constraints = ref<MediaConstraints>({ video: true, audio: true });
  const WebRTC = new StreamingHelper();
  const streamClosed = ref(true);
  let tryChangeDeviceCount = 0;

  const produce = computed(() => !!connectionData.value);

  const getLoggerContext = (): Record<string, any> => {
    return {
      stream: { id: stream.value.id, active: stream.value.active },
      connection: connectionData.value,
      constraints: constraints.value,
      ...getAdditionalLoggerContext(),
    };
  };
  const { businessLogicLog, collectErrorLogs, debug } = useStreamLogger("[PRODUCE CONTROLLER]", getLoggerContext);

  const createLocalStream = async () => {
    try {
      stream.value = await WebRTC.getStream(constraints.value);
    } catch (error) {
      collectErrorLogs("Create local stream", error);
    }
    streamClosed.value = false;
  };

  const closeStreams = (stop = true) => {
    businessLogicLog("Close streams");
    WebRTC.closeAllStreams();
    if (stop && stream.value.getTracks().length > 0) {
      stream.value.getTracks().forEach(t => t.stop());
    }
    streamClosed.value = true;
  };

  const reset = () => {
    closeStreams();
    connectionData.value = null;
  };

  const produceStream = async (token: string) => {
    try {
      if (onCreatedPromise.value) await onCreatedPromise.value;
      if (connectionData.value) {
        businessLogicLog("Reconnect");
        closeStreams();
      }
      if (streamClosed.value) {
        await createLocalStream();
      }
      businessLogicLog("Produce stream");
      connecting.value = true;
      connectionData.value = await providers.LivechatsProvider.broadcast({
        data: { token, broadcasting: true },
      });
      if (connectionData.value) {
        stream.value = await WebRTC.produceStream(connectionData.value, stream.value);
      }
    } catch (error) {
      collectErrorLogs("Produce stream", error);
    } finally {
      connecting.value = false;
    }
  };

  const stopBroadcasting = async (token: string) => {
    try {
      closeStreams(false);
      connectionData.value = await providers.LivechatsProvider.broadcast({
        data: { token, broadcasting: false },
      });
    } finally {
      connecting.value = false;
    }
  };

  const onChangeDevice = async (newConstraints: MediaConstraints) => {
    businessLogicLog("Change device");
    const localConstrains = {
      ...constraints.value,
      ...newConstraints,
    };
    if (!isEqual(constraints.value.video, localConstrains.video)) {
      stream.value.getVideoTracks().forEach(t => {
        t.stop();
        stream.value.removeTrack(t);
      });
    }
    if (!isEqual(constraints.value.audio, localConstrains.audio)) {
      stream.value.getAudioTracks().forEach(t => {
        t.stop();
        stream.value.removeTrack(t);
      });
    }
    const oldConstraints = constraints.value;
    constraints.value = localConstrains;
    try {
      if (connectionData.value) {
        stream.value = await WebRTC.produceStream(
          connectionData.value,
          !localConstrains.video && !localConstrains.audio ? new MediaStream([]) : localConstrains
        );
        tryChangeDeviceCount = 0;
      } else {
        await createLocalStream();
      }
    } catch (error) {
      collectErrorLogs("Change device", error);
      if (tryChangeDeviceCount < 3) {
        tryChangeDeviceCount++;
        onChangeDevice(oldConstraints);
      }
    }
  };

  const initStream = async () => {
    try {
      businessLogicLog("Create stream");
      onCreatedPromise.value = createLocalStream();
      await onCreatedPromise.value;
    } catch (error) {
      collectErrorLogs("Create stream", error);
    } finally {
      onCreatedPromise.value = undefined;
    }
  };

  WebRTC.logger.on("logging", ({ message, ...others }: LoggerContextWithMessage) =>
    debug(`[STREAM CLIENT] ${message}`, others)
  );

  WebRTC.on("connectionstatechange", (state: string) => (connectingState.value = state));

  onUnmounted(() => {
    closeStreams();
  });

  return {
    stream,
    connecting,
    connectingState,
    constraints,
    produce,
    produceStream,
    stopBroadcasting,
    onChangeDevice,
    reset,
    createLocalStream,
    initStream,
  };
}
